import React from 'react';
import { makeStyles } from '@material-ui/core';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import LocalPharmacyIcon from '@material-ui/icons/LocalPharmacy';
import { ComputedPlan } from './types';
import { formatMinutes, formatTime } from './compute';

const useStyles = makeStyles({
  root: {
    padding: '0.5em 0',
  },
  empty: {
    padding: '0.75em 1em',
    color: '#666',
    fontSize: '0.9em',
    textAlign: 'center',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '0.35em 0.9em',
    fontSize: '0.85em',
  },
  rowAnchor: {
    fontWeight: 600,
    color: '#5b2a82',
  },
  time: {
    flex: '0 0 78px',
    fontVariantNumeric: 'tabular-nums',
    color: '#444',
    whiteSpace: 'nowrap',
  },
  label: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  duration: {
    color: '#666',
    fontSize: '0.85em',
    whiteSpace: 'nowrap',
  },
  divider: {
    height: 1,
    background: '#eee',
    margin: '0.25em 0',
  },
});

interface Props {
  computed: ComputedPlan;
  hasAppt: boolean;
}

export default function Timeline({ computed, hasAppt }: Props) {
  const classes = useStyles();
  if (!hasAppt) {
    return <div className={classes.empty}>Set an appointment time to see the timeline.</div>;
  }
  return (
    <div className={classes.root}>
      <div className={`${classes.row} ${classes.rowAnchor}`}>
        <span className={classes.time}>{formatTime(computed.leaveByAt)}</span>
        <span className={classes.label}>Start</span>
      </div>
      {computed.steps.length > 0 && <div className={classes.divider} />}
      {computed.steps.map(cs => {
        const isDriving = cs.step.type === 'driving';
        const isMedicine = !!cs.isMedicine;
        return (
          <div className={classes.row} key={cs.step.id}>
            <span className={classes.time}>{formatTime(cs.startsAt)}</span>
            <span className={classes.label}>
              {isDriving ? (
                <DirectionsCarIcon style={{ fontSize: '1em', verticalAlign: 'middle', marginRight: 4 }} />
              ) : isMedicine ? (
                <LocalPharmacyIcon style={{ fontSize: '1em', verticalAlign: 'middle', marginRight: 4 }} />
              ) : null}
              {cs.step.name || (isDriving ? 'Drive' : 'Step')}
            </span>
            {!isMedicine && (
              <span className={classes.duration}>{formatMinutes(cs.effectiveMinutes)}</span>
            )}
          </div>
        );
      })}
      {computed.steps.length > 0 && <div className={classes.divider} />}
      <div className={classes.row}>
        <span className={classes.time}>{formatTime(computed.arriveAt)}</span>
        <span className={classes.label}>Arrive</span>
      </div>
      <div className={`${classes.row} ${classes.rowAnchor}`}>
        <span className={classes.time}>{formatTime(computed.appointmentAt)}</span>
        <span className={classes.label}>Appointment</span>
      </div>
    </div>
  );
}
